import React, { useState, useMemo } from 'react';
import { ChevronDown, AlertTriangle, Phone, MessageSquare, Plus, Zap, Send, CheckCircle, Clock, Shield, AlertCircle, Wrench } from 'lucide-react';
import { theme } from '../../lib/theme';
import Badge from '../common/Badge';
import EmptyState from '../common/EmptyState';
import LoadingSpinner from '../common/LoadingSpinner';
import { MAINT_STATUSES, MAINT_PRIORITIES, MAINT_CATEGORIES } from '../../lib/constants';
import { formatDate } from '../../lib/dateFormat';

// ============================================================
// AI TRIAGE ENGINE — Rule-based categorization & prioritization
// ============================================================

const TRIAGE_RULES = {
  emergency: {
    keywords: ['fire', 'flood', 'gas leak', 'no heat', 'sewage', 'carbon monoxide', 'electrical fire', 'smoke', 'broken lock', 'break-in', 'burst pipe', 'gas smell', 'ceiling collapse', 'mold black'],
    label: 'Emergency',
    color: '#DC2626',
    response: 'Dispatch immediately — first available vendor',
  },
  urgent: {
    keywords: ['no hot water', 'ac not cooling', 'water heater', 'leak', 'no power', 'security', 'toilet overflow', 'pipe burst', 'no electricity', 'heater broken', 'refrigerator not working', 'freezer'],
    label: 'Urgent',
    color: '#EA580C',
    response: 'Contact vendor within 2 hours',
  },
  medium: {
    keywords: ['faucet', 'toilet', 'appliance', 'disposal', 'dishwasher', 'dryer', 'washer', 'clogged', 'slow drain', 'running toilet', 'window stuck', 'door lock', 'garbage disposal'],
    label: 'Medium',
    color: '#EAB308',
    response: 'Schedule within 24-48 hours',
  },
  low: {
    keywords: ['paint', 'cosmetic', 'screen', 'door squeak', 'cabinet', 'shelf', 'caulk', 'touch up', 'minor', 'scratch', 'dent', 'light bulb', 'weather strip'],
    label: 'Low',
    color: '#16A34A',
    response: 'Schedule at next convenience',
  },
};

const CATEGORY_RULES = {
  plumbing: { keywords: ['faucet', 'toilet', 'pipe', 'drain', 'water', 'plumb', 'leak', 'sewage', 'water heater', 'hot water', 'clog', 'disposal'], cost: '$150–400' },
  hvac: { keywords: ['ac', 'air condition', 'heat', 'hvac', 'furnace', 'thermostat', 'cooling', 'vent', 'duct', 'boiler'], cost: '$200–800' },
  electrical: { keywords: ['electric', 'outlet', 'switch', 'breaker', 'power', 'light', 'wire', 'circuit', 'spark', 'flickering'], cost: '$100–350' },
  appliance: { keywords: ['refrigerator', 'fridge', 'dishwasher', 'washer', 'dryer', 'oven', 'stove', 'microwave', 'freezer', 'garbage disposal'], cost: '$150–500' },
  safety: { keywords: ['fire', 'smoke', 'carbon monoxide', 'gas', 'lock', 'security', 'alarm', 'detector', 'break-in', 'mold'], cost: '$100–400' },
  exterior: { keywords: ['roof', 'gutter', 'siding', 'window', 'door', 'fence', 'deck', 'porch', 'driveway', 'landscap'], cost: '$200–600' },
  pest: { keywords: ['pest', 'bug', 'ant', 'roach', 'mouse', 'mice', 'rat', 'termite', 'bee', 'wasp', 'spider'], cost: '$100–300' },
  general: { keywords: [], cost: '$75–250' },
};

function runAITriage(description) {
  const desc = (description || '').toLowerCase();

  // Determine priority
  let priority = 'low';
  let priorityInfo = TRIAGE_RULES.low;
  for (const [level, rule] of Object.entries(TRIAGE_RULES)) {
    if (rule.keywords.some(kw => desc.includes(kw))) {
      priority = level;
      priorityInfo = rule;
      break; // First match wins (emergency > urgent > medium > low)
    }
  }

  // Determine category
  let category = 'general';
  let categoryInfo = CATEGORY_RULES.general;
  for (const [cat, rule] of Object.entries(CATEGORY_RULES)) {
    if (cat === 'general') continue;
    if (rule.keywords.some(kw => desc.includes(kw))) {
      category = cat;
      categoryInfo = rule;
      break;
    }
  }

  return {
    priority,
    priorityLabel: priorityInfo.label,
    priorityColor: priorityInfo.color,
    responseTime: priorityInfo.response,
    category: category.charAt(0).toUpperCase() + category.slice(1),
    estimatedCost: categoryInfo.cost,
    suggestedAction: priority === 'emergency'
      ? 'IMMEDIATE: Dispatch first available vendor. Notify owner if cost may exceed threshold.'
      : priority === 'urgent'
        ? 'Contact preferred vendor immediately. Send tenant acknowledgment SMS.'
        : priority === 'medium'
          ? 'Request quotes from 2-3 vendors. Schedule within 48 hours.'
          : 'Add to routine maintenance queue. Schedule at next visit.',
    confidence: priority === 'emergency' || priority === 'urgent' ? 95 : 85,
  };
}

// ============================================================
// MAIN COMPONENT
// ============================================================

const MaintenanceView = ({
  requests = [],
  vendorQuotes = {},
  propertyVendors = {},
  onUpdateRequest = () => {},
  showToast,
  demoMode,
}) => {
  const [expandedRequestId, setExpandedRequestId] = useState(null);
  const [triageResults, setTriageResults] = useState({}); // { requestId: triageResult }
  const [sendingSMS, setSendingSMS] = useState(null);
  const [triagingAll, setTriagingAll] = useState(false);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newRequest, setNewRequest] = useState({
    property: '', unit: '', tenant_name: '', tenant_phone: '', description: '',
  });

  // Summary stats
  const stats = useMemo(() => {
    const emergency = requests.filter(r => r.priority === 'emergency' || r.priority === 'urgent').length;
    const newRequests = requests.filter(r => r.status === 'New' || r.status === 'new').length;
    const inProgress = requests.filter(r => ['In Progress', 'in_progress', 'AI Dispatched', 'ai_dispatched'].includes(r.status)).length;
    return { total: requests.length, emergency, newRequests, inProgress };
  }, [requests]);

  // Run triage on a single request
  const handleTriage = (request) => {
    const description = request.issueDescription || request.issue || '';
    const result = runAITriage(description);
    setTriageResults(prev => ({ ...prev, [request.id]: result }));
    if (showToast) showToast(`AI Triage: ${result.priorityLabel} priority — ${result.category}`);
  };

  // Run triage on all untriaged requests
  const handleTriageAll = () => {
    setTriagingAll(true);
    const newResults = {};
    let count = 0;
    requests.forEach(req => {
      if (!triageResults[req.id]) {
        const description = req.issueDescription || req.issue || '';
        newResults[req.id] = runAITriage(description);
        count++;
      }
    });
    setTriageResults(prev => ({ ...prev, ...newResults }));
    setTriagingAll(false);
    if (showToast) showToast(`AI triaged ${count} request(s)`);
  };

  // Send SMS acknowledgment to tenant
  const handleSendSMS = async (request) => {
    setSendingSMS(request.id);
    if (demoMode) {
      setTimeout(() => {
        if (showToast) showToast(`SMS sent to ${request.tenantName}! (Demo)`);
        setSendingSMS(null);
      }, 500);
      return;
    }

    try {
      const smsLib = await import('../../lib/sms.js');
      const tenant = request._raw?.tenant || { first_name: request.tenantName, phone: request.tenantPhone };
      const property = request.propertyAddress || 'your property';
      const result = await smsLib.sendMaintenanceUpdate(tenant, property, 'received');
      if (result.success) {
        if (showToast) showToast(`SMS sent to ${request.tenantName}`);
      } else {
        if (showToast) showToast(`SMS failed: ${result.error}`, true);
      }
    } catch (err) {
      if (showToast) showToast('SMS error: ' + err.message, true);
    }
    setSendingSMS(null);
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'emergency': return theme.colors.danger;
      case 'urgent': return theme.colors.danger;
      case 'medium': return theme.colors.warning;
      case 'low': return theme.colors.success;
      default: return theme.colors.info;
    }
  };

  const getPriorityLabel = (priority) => {
    return priority?.charAt(0).toUpperCase() + priority?.slice(1).toLowerCase() || 'Low';
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    if (diffInHours < 24) {
      const diffInMinutes = Math.floor(diffInHours * 60);
      return diffInMinutes < 1 ? 'just now' : `${diffInMinutes}m ago`;
    } else if (diffInHours < 168) {
      return `${Math.floor(diffInHours / 24)}d ago`;
    }
    return formatDate(timestamp);
  };

  const getVendorStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'confirmed': return theme.colors.success;
      case 'quoted': return theme.colors.warning;
      case 'pending': return theme.colors.info;
      default: return theme.colors.textLight;
    }
  };

  const renderVendorPreferences = (propertyId) => {
    const vendors = propertyVendors[propertyId];
    if (!vendors) return null;
    const vendorEmojis = { required: '⭐', preferred: '👍', approved: '✓', blocked: '🚫' };
    const vendorCategories = [
      { key: 'required', label: 'Required', icon: vendorEmojis.required },
      { key: 'preferred', label: 'Preferred', icon: vendorEmojis.preferred },
      { key: 'approved', label: 'Approved', icon: vendorEmojis.approved },
      { key: 'blocked', label: 'Blocked', icon: vendorEmojis.blocked },
    ];
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '16px', paddingBottom: '12px', borderBottom: `1px solid ${theme.colors.border}` }}>
        {vendorCategories.map(({ key, label, icon }) => {
          const vendorList = vendors[key] || [];
          if (vendorList.length === 0) return null;
          return (
            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: theme.colors.textLight }}>
              <span>{icon}</span>
              <span style={{ fontWeight: '500' }}>{label}:</span>
              <span>{vendorList.join(', ')}</span>
            </div>
          );
        })}
      </div>
    );
  };

  const renderVendorQuotes = (requestId, propertyId) => {
    const quotes = vendorQuotes[requestId] || [];
    if (quotes.length === 0) {
      return (
        <div style={{ padding: '16px', textAlign: 'center', color: theme.colors.textLight, fontSize: '14px' }}>
          No vendor quotes yet
        </div>
      );
    }
    return quotes.map((quote, index) => {
      const isFirstResponder = quote.isFirstResponder;
      return (
        <div key={index} style={{
          padding: '12px 16px', marginBottom: '8px',
          backgroundColor: isFirstResponder ? '#FEF3C7' : theme.colors.bg,
          border: `1px solid ${isFirstResponder ? theme.colors.warning : theme.colors.border}`,
          borderRadius: theme.radius.md,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              <span style={{ fontWeight: '600', color: theme.colors.text }}>{quote.vendorName}</span>
              {isFirstResponder && (
                <span style={{ fontSize: '11px', fontWeight: '600', backgroundColor: theme.colors.warning, color: '#FFF', padding: '2px 8px', borderRadius: '4px' }}>
                  First Responder
                </span>
              )}
              <span style={{ fontSize: '12px', padding: '2px 8px', backgroundColor: getVendorStatusColor(quote.status), color: '#FFF', borderRadius: '4px' }}>
                {quote.status?.charAt(0).toUpperCase() + quote.status?.slice(1).toLowerCase()}
              </span>
            </div>
            <div style={{ fontSize: '12px', color: theme.colors.textLight, marginBottom: '4px' }}>
              {quote.availability && <span>Available: {quote.availability}</span>}
            </div>
            <div style={{ fontSize: '12px', color: theme.colors.textLight }}>
              Rating: {'★'.repeat(Math.floor(quote.rating || 0))} ({quote.rating || 0}/5)
            </div>
          </div>
          <div style={{ textAlign: 'right', marginLeft: '16px' }}>
            <div style={{ fontSize: '18px', fontWeight: '700', color: theme.colors.primary, marginBottom: '8px' }}>
              ${quote.amount}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button style={{ padding: '6px 12px', fontSize: '12px', backgroundColor: theme.colors.primary, color: '#FFF', border: 'none', borderRadius: theme.radius.md, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }} title="Contact vendor">
                <Phone size={14} />
              </button>
              <button style={{ padding: '6px 12px', fontSize: '12px', backgroundColor: theme.colors.info, color: '#FFF', border: 'none', borderRadius: theme.radius.md, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }} title="Message vendor">
                <MessageSquare size={14} />
              </button>
            </div>
          </div>
        </div>
      );
    });
  };

  // Handle new request submission
  const handleNewRequest = (e) => {
    e.preventDefault();
    if (!newRequest.description.trim()) {
      showToast?.('Please enter a description of the issue');
      return;
    }

    // Run AI triage on the new request
    const triage = runAITriage(newRequest.description);
    const newId = `maint-${Date.now()}`;

    const request = {
      id: newId,
      property_address: newRequest.property || 'Unassigned Property',
      unit: newRequest.unit || '',
      tenant_name: newRequest.tenant_name || 'Walk-in',
      tenant_phone: newRequest.tenant_phone || '',
      description: newRequest.description,
      status: 'New',
      priority: triage.priority,
      category: triage.category,
      created_at: new Date().toISOString(),
      source: 'Manual Entry',
    };

    // In demo mode, add to local state via parent
    onUpdateRequest(request, 'add');
    setTriageResults(prev => ({ ...prev, [newId]: triage }));

    showToast?.(`New request created — AI triaged as ${triage.priorityLabel} (${triage.category})`);
    setNewRequest({ property: '', unit: '', tenant_name: '', tenant_phone: '', description: '' });
    setShowNewForm(false);
  };

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: 8 }}>
        <h1 style={{ margin: 0, color: theme.colors.text, fontSize: '24px', fontWeight: '700' }}>
          Maintenance Requests
        </h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            data-tour="maint-ai"
            onClick={handleTriageAll}
            disabled={triagingAll}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '8px 14px', backgroundColor: '#7C3AED', color: '#FFF',
              border: 'none', borderRadius: theme.radius.md, fontSize: '13px',
              fontWeight: '600', cursor: 'pointer',
            }}
          >
            <Zap size={15} />
            {triagingAll ? 'Triaging...' : 'AI Triage All'}
          </button>
          <button
            onClick={() => setShowNewForm(!showNewForm)}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '8px 14px', backgroundColor: showNewForm ? theme.colors.textLight : theme.colors.primary, color: '#FFF',
              border: 'none', borderRadius: theme.radius.md, fontSize: '13px',
              fontWeight: '600', cursor: 'pointer', boxShadow: theme.shadow.sm,
            }}
          >
            <Plus size={16} />
            {showNewForm ? 'Cancel' : 'New Request'}
          </button>
        </div>
      </div>

      {/* New Request Form */}
      {showNewForm && (
        <div style={{
          background: theme.colors.card, borderRadius: theme.radius.lg,
          padding: 20, border: `1px solid ${theme.colors.border}`,
          marginBottom: 16, boxShadow: theme.shadow.sm,
        }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: theme.colors.text, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Plus size={16} color={theme.colors.primary} /> New Maintenance Request
          </div>
          <form onSubmit={handleNewRequest}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: theme.colors.textLight, display: 'block', marginBottom: 3 }}>Property Address *</label>
                <input type="text" value={newRequest.property} onChange={e => setNewRequest(p => ({ ...p, property: e.target.value }))}
                  placeholder="302 Elm Street" style={{ width: '100%', padding: '8px 10px', borderRadius: 7, border: `1px solid ${theme.colors.border}`, fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: theme.colors.textLight, display: 'block', marginBottom: 3 }}>Unit #</label>
                <input type="text" value={newRequest.unit} onChange={e => setNewRequest(p => ({ ...p, unit: e.target.value }))}
                  placeholder="Apt 2B (optional)" style={{ width: '100%', padding: '8px 10px', borderRadius: 7, border: `1px solid ${theme.colors.border}`, fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: theme.colors.textLight, display: 'block', marginBottom: 3 }}>Tenant Name</label>
                <input type="text" value={newRequest.tenant_name} onChange={e => setNewRequest(p => ({ ...p, tenant_name: e.target.value }))}
                  placeholder="Carlos Rivera" style={{ width: '100%', padding: '8px 10px', borderRadius: 7, border: `1px solid ${theme.colors.border}`, fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: theme.colors.textLight, display: 'block', marginBottom: 3 }}>Tenant Phone</label>
                <input type="tel" value={newRequest.tenant_phone} onChange={e => setNewRequest(p => ({ ...p, tenant_phone: e.target.value }))}
                  placeholder="856-555-0100" style={{ width: '100%', padding: '8px 10px', borderRadius: 7, border: `1px solid ${theme.colors.border}`, fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
              </div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: theme.colors.textLight, display: 'block', marginBottom: 3 }}>
                Issue Description * <span style={{ fontWeight: 400, color: theme.colors.accent }}>(AI will auto-triage priority & category)</span>
              </label>
              <textarea value={newRequest.description} onChange={e => setNewRequest(p => ({ ...p, description: e.target.value }))}
                placeholder="Describe the maintenance issue in detail... e.g., 'Leaking kitchen faucet, dripping constantly, water pooling under sink'"
                rows={3}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 7, border: `1px solid ${theme.colors.border}`, fontSize: 13, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical' }} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button type="submit" style={{
                padding: '10px 20px', borderRadius: 8, border: 'none',
                background: theme.colors.success, color: 'white',
                fontSize: 13, fontWeight: 600, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <Zap size={14} /> Create & AI Triage
              </button>
              <span style={{ fontSize: 11, color: theme.colors.textLight }}>
                AI will automatically categorize, set priority, and suggest next steps
              </span>
            </div>
          </form>
        </div>
      )}

      {/* Summary Stats */}
      <div data-tour="maint-stats" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginBottom: 20 }}>
        <div style={{ background: theme.colors.card, borderRadius: theme.radius.md, padding: 14, boxShadow: theme.shadow.sm, textAlign: 'center' }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: theme.colors.text }}>{stats.total}</div>
          <div style={{ fontSize: 10, color: theme.colors.textLight }}>Total Requests</div>
        </div>
        <div style={{
          background: theme.colors.card, borderRadius: theme.radius.md, padding: 14,
          boxShadow: theme.shadow.sm, textAlign: 'center',
          border: stats.emergency > 0 ? `2px solid ${theme.colors.danger}40` : `1px solid ${theme.colors.border}`,
        }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: stats.emergency > 0 ? theme.colors.danger : theme.colors.text }}>{stats.emergency}</div>
          <div style={{ fontSize: 10, color: theme.colors.textLight }}>Emergency/Urgent</div>
        </div>
        <div style={{ background: theme.colors.card, borderRadius: theme.radius.md, padding: 14, boxShadow: theme.shadow.sm, textAlign: 'center' }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: theme.colors.info }}>{stats.newRequests}</div>
          <div style={{ fontSize: 10, color: theme.colors.textLight }}>New (Untriaged)</div>
        </div>
        <div style={{ background: theme.colors.card, borderRadius: theme.radius.md, padding: 14, boxShadow: theme.shadow.sm, textAlign: 'center' }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: theme.colors.primary }}>{stats.inProgress}</div>
          <div style={{ fontSize: 10, color: theme.colors.textLight }}>In Progress</div>
        </div>
      </div>

      {/* Empty state */}
      {requests.length === 0 ? (
        <EmptyState
          icon={<Wrench size={30} color={theme.colors.primary} />}
          title="No maintenance requests"
          description="Work orders from tenants and property inspections will appear here."
        />
      ) : (
        <div data-tour="maint-card" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {requests.map((request) => {
            // Use stored triage result, or auto-generate from existing data if request already has priority/category
            const triage = triageResults[request.id] || (
              (request.priority && request.priority !== 'new') ? runAITriage(request.description || request.title || '') : null
            );
            const isExpanded = expandedRequestId === request.id;

            return (
              <div key={request.id} style={{
                backgroundColor: theme.colors.card,
                border: `1px solid ${theme.colors.border}`,
                borderRadius: theme.radius.md,
                overflow: 'hidden', boxShadow: theme.shadow.sm,
              }}>
                {/* Main request card */}
                <div
                  style={{
                    padding: '14px 16px', display: 'flex', alignItems: 'flex-start', gap: '12px',
                    cursor: 'pointer',
                    backgroundColor: isExpanded ? '#F9FAFB' : theme.colors.card,
                  }}
                  onClick={() => setExpandedRequestId(isExpanded ? null : request.id)}
                >
                  {/* Priority indicator */}
                  <div style={{
                    width: '4px', backgroundColor: getPriorityColor(request.priority),
                    borderRadius: '2px', minHeight: '60px',
                  }} />

                  {/* Request content */}
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px', flexWrap: 'wrap', gap: 6 }}>
                      <h3 style={{ margin: 0, color: theme.colors.text, fontSize: '14px', fontWeight: '600' }}>
                        {request.issueDescription || request.issue}
                      </h3>
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                        {request.category && (
                          <span style={{
                            fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 8,
                            background: `${theme.colors.info}15`, color: theme.colors.info,
                          }}>
                            {request.category}
                          </span>
                        )}
                        <Badge variant={request.status?.toLowerCase()} label={request.status} />
                        <Badge
                          variant="priority"
                          label={getPriorityLabel(request.priority)}
                          style={{ backgroundColor: getPriorityColor(request.priority), color: '#FFF' }}
                        />
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: theme.colors.textLight, flexWrap: 'wrap' }}>
                      <span>{request.propertyAddress}</span>
                      <span>{request.tenantName}</span>
                      <span>{formatTime(request.createdAt)}</span>
                      {request.vendorName && <span style={{ color: theme.colors.primary }}>Vendor: {request.vendorName}</span>}
                    </div>
                  </div>

                  <ChevronDown size={18} style={{
                    color: theme.colors.textLight,
                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease', marginTop: '2px', flexShrink: 0,
                  }} />
                </div>

                {/* AI Triage Results (shown when triaged, even when collapsed) */}
                {triage && !isExpanded && (
                  <div style={{
                    padding: '8px 16px 8px 32px',
                    background: `${triage.priorityColor}08`,
                    borderTop: `1px solid ${triage.priorityColor}20`,
                    display: 'flex', gap: 12, alignItems: 'center', fontSize: 11, flexWrap: 'wrap',
                  }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Zap size={12} color="#7C3AED" />
                      <strong style={{ color: '#7C3AED' }}>AI Triage:</strong>
                    </span>
                    <span style={{ fontWeight: 600, color: triage.priorityColor }}>{triage.priorityLabel}</span>
                    <span style={{ color: theme.colors.text }}>{triage.category}</span>
                    <span style={{ color: theme.colors.textLight }}>Est: {triage.estimatedCost}</span>
                    <span style={{ color: theme.colors.textLight }}>{triage.confidence}% confidence</span>
                  </div>
                )}

                {/* Expanded content */}
                {isExpanded && (
                  <div style={{ backgroundColor: theme.colors.bg, borderTop: `1px solid ${theme.colors.border}`, padding: '16px' }}>

                    {/* AI Triage Section */}
                    {triage ? (
                      <div style={{
                        padding: '14px', borderRadius: theme.radius.md, marginBottom: '16px',
                        background: `linear-gradient(135deg, #7C3AED08, ${triage.priorityColor}08)`,
                        border: `1px solid #7C3AED30`,
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                          <Zap size={16} color="#7C3AED" />
                          <span style={{ fontSize: 13, fontWeight: 700, color: '#7C3AED' }}>AI Triage Results</span>
                          <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 8, background: '#7C3AED15', color: '#7C3AED' }}>
                            {triage.confidence}% confidence
                          </span>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 10 }}>
                          <div>
                            <div style={{ fontSize: 10, color: theme.colors.textLight, marginBottom: 2 }}>Priority</div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: triage.priorityColor }}>{triage.priorityLabel}</div>
                          </div>
                          <div>
                            <div style={{ fontSize: 10, color: theme.colors.textLight, marginBottom: 2 }}>Category</div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: theme.colors.text }}>{triage.category}</div>
                          </div>
                          <div>
                            <div style={{ fontSize: 10, color: theme.colors.textLight, marginBottom: 2 }}>Est. Cost</div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: theme.colors.primary }}>{triage.estimatedCost}</div>
                          </div>
                          <div>
                            <div style={{ fontSize: 10, color: theme.colors.textLight, marginBottom: 2 }}>Response Time</div>
                            <div style={{ fontSize: 11, fontWeight: 500, color: theme.colors.text }}>{triage.responseTime}</div>
                          </div>
                        </div>
                        <div style={{ fontSize: 11, color: theme.colors.text, padding: '8px 10px', borderRadius: theme.radius.sm, background: 'white', border: `1px solid ${theme.colors.border}` }}>
                          <strong>Suggested Action:</strong> {triage.suggestedAction}
                        </div>

                        {/* Action buttons + Re-triage */}
                        <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleSendSMS(request); }}
                            disabled={sendingSMS === request.id}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 4,
                              padding: '5px 10px', borderRadius: theme.radius.sm,
                              border: `1px solid ${theme.colors.border}`,
                              background: 'white', cursor: 'pointer',
                              fontSize: 11, fontWeight: 500, color: theme.colors.text,
                            }}
                          >
                            <Send size={12} />
                            {sendingSMS === request.id ? 'Sending...' : 'SMS Tenant'}
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); }}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 4,
                              padding: '5px 10px', borderRadius: theme.radius.sm,
                              border: 'none', background: theme.colors.primary,
                              cursor: 'pointer', fontSize: 11, fontWeight: 600, color: 'white',
                            }}
                          >
                            <Phone size={12} />
                            Dispatch Vendor
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleTriage(request); }}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 4,
                              padding: '5px 10px', borderRadius: theme.radius.sm,
                              border: `1px solid ${theme.colors.border}`,
                              background: 'white', cursor: 'pointer',
                              fontSize: 11, fontWeight: 500, color: '#7C3AED',
                            }}
                          >
                            <Zap size={12} />
                            Re-triage
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleTriage(request); }}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 6, width: '100%',
                          padding: '12px 16px', marginBottom: '16px',
                          borderRadius: theme.radius.md,
                          border: '2px dashed #7C3AED40',
                          background: '#7C3AED08', cursor: 'pointer',
                          fontSize: 13, fontWeight: 600, color: '#7C3AED',
                          justifyContent: 'center',
                        }}
                      >
                        <Zap size={16} />
                        Run AI Triage on This Request
                      </button>
                    )}

                    {/* Vendor preferences bar */}
                    {renderVendorPreferences(request.propertyId)}

                    {/* Vendor quotes section */}
                    <div>
                      <h4 style={{ margin: '0 0 12px 0', color: theme.colors.text, fontSize: '14px', fontWeight: '600' }}>
                        Vendor Quotes
                      </h4>
                      {renderVendorQuotes(request.id, request.propertyId)}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MaintenanceView;
